import { useMemo } from 'react';

import { useIntl } from 'react-intl';

import {
	type HubBreadcrumbItem,
	useRegisterHubBreadcrumbs,
} from '@atlassian/company-hub-common/hub-breadcrumbs-context';
import {
	HUB_FEATURES,
	useIsHubFeatureEnabled,
} from '@atlassian/company-hub-common/use-is-hub-feature-enabled';

import { staticHubBreadcrumbMessages } from '../static-hub-breadcrumbs/messages';

export interface HubBreadcrumbDefinition {
	href: string;
	id: string;
	text: string | null | undefined;
}

export const getHubBreadcrumbItems = ({
	currentItem,
	homeText,
	ancestorItems = [],
}: {
	ancestorItems?: readonly HubBreadcrumbDefinition[];
	currentItem: HubBreadcrumbDefinition | undefined;
	homeText: string;
}): readonly HubBreadcrumbItem[] => {
	if (!currentItem) {
		return [];
	}

	const normalizedHierarchy = [...ancestorItems, currentItem].map((item) => ({
		...item,
		text: item.text?.trim(),
	}));

	if (normalizedHierarchy.some((item) => !item.text)) {
		return [];
	}

	return [
		{
			href: '/',
			id: 'home',
			text: homeText,
		},
		...normalizedHierarchy.map((item) => ({
			href: item.href,
			id: item.id,
			text: item.text ?? '',
		})),
	];
};

export const useAreHubBreadcrumbsEnabled = (): boolean => {
	const [isBreadcrumbsEnabled] = useIsHubFeatureEnabled([HUB_FEATURES.BREADCRUMBS]);

	return isBreadcrumbsEnabled;
};

export const useHubBreadcrumbs = ({
	ancestorItems,
	currentItem,
}: {
	ancestorItems?: readonly HubBreadcrumbDefinition[];
	currentItem: HubBreadcrumbDefinition | undefined;
}): void => {
	const { formatMessage } = useIntl();
	const isBreadcrumbsEnabled = useAreHubBreadcrumbsEnabled();

	const items = useMemo<readonly HubBreadcrumbItem[]>(() => {
		if (!isBreadcrumbsEnabled) {
			return [];
		}

		return getHubBreadcrumbItems({
			ancestorItems,
			currentItem,
			homeText: formatMessage(staticHubBreadcrumbMessages.home),
		});
	}, [ancestorItems, currentItem, formatMessage, isBreadcrumbsEnabled]);

	useRegisterHubBreadcrumbs(items);
};
